// Libraries
import { useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  EmailAuthProvider,
  getAuth,
  signInWithCredential,
  signInWithCustomToken,
} from 'firebase/auth'
import { where } from 'firebase/firestore'
import { useFieldArray, useForm } from 'react-hook-form'
import { useHistory } from 'react-router'
import * as yup from 'yup'
import { Visibility, VisibilityOffOutlined } from '@mui/icons-material'

// Common
import MainModal from '../common/MainModal'
import { SwitchAccountFirstStep } from './SwitchAccountFirstStep'
import { SwitchAccountWhoHasAccess } from './SwitchAccountWhoHasAccess'
import MessageHelperComp from '../../../helpers/MessageHelper'
import { ESwitchScreen, SwitchAccountTabs } from './SwitchAccountTabs'

// Helpers
import useToasterHelper from '../../../helpers/ToasterHelper'
import { getUserFullName } from '../../../helpers/helpers'
import { getConvertedData } from '../../../models/interface.helper'

// Models
import { UserModel } from '../../../models/users/user.model'
import { ProfileModel } from '../../../models/profiles/profile.model'

import { ISwitchProfile, IProfileHasAccess, IRemoveProfile } from './types/switchAccountTypes'
import { IProfile } from '../../../models/profiles/profile.interface'

// Services
import FirestoreService from '../../../services/firestoreService'
import UserService from '../../../services/firebase-service/users/userService'
import { httpService } from '../../../services/httpService'
import { useCreate } from '../../../hooks/useCreate'

// Store
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { storeUseMode } from '../../../store/system/systemThunk'
import {
  selectUserId,
  setUserId,
  setProfileDetails,
  storeUserId,
} from '../../../store/user/userSlice'

// CONSTS
import { CONST } from '../../../const/const'
import { MESSAGES_CONST } from '../../../const/messages-const'
import { MODAL_CONSTS } from '../../../const/modal-const'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

type IProps = {
  show: boolean
  handleModal: (show: boolean, modal_name: string, data?: any) => void
  dataToPassOn: any
}

interface IRespLoginUserInfo {
  uid: string
  lastSignInTime: IProfileHasAccess['lastSignInTime']
}

const formschema = yup.object().shape({
  formData: yup.object().shape({
    email: yup.string().email('provide valid email').required('This field is required'),
    password: yup.string().required('This field is required'),
  }),
  message: yup.string(),
  selectedProfileIndex: yup.number().nullable(),
  profiles: yup
    .array(
      yup.object().shape({
        userDocId: yup.string(),
        name: yup.string().nullable(),
        userType: yup.string().nullable(),
        userProfilePicture: yup.string().nullable(),
      })
    )
    .nullable(),
})

const FORM_DEFAULT = {
  formData: {
    email: '',
    password: '',
  },
  message: '',
  selectedProfileIndex: null as number | null,
  profiles: [] as ISwitchProfile[],
  hasAccessProfiles: [] as IProfileHasAccess[],
}

const getProfileUsers = async (userId: string, allowedUids: string[]) => {
  const profiles: IProfile[] = []

  const profilesSnap = await FirestoreService.filterItems(COLLECTIONS.PROFILES.NAME, [
    where(COLLECTIONS.PROFILES.FIELDS.ALLOWED_UIDS.KEY, 'array-contains', userId),
  ])

  profilesSnap.forEach((item) => {
    profiles.push(getConvertedData(ProfileModel.fromFirestoreDoc(item)))
  })

  const ids: Set<string> = new Set()

  ids.add(userId)

  allowedUids.forEach((id) => {
    ids.add(id)
  })

  profiles.forEach((item) => {
    if (item.id) ids.add(item.id)
  })

  const users = await UserService.getByIds([...ids])

  return { users, profiles }
}

const SwitchAccounts = (props: IProps) => {
  const userId = useAppSelector(selectUserId)
  const history = useHistory()
  const dispatch = useAppDispatch()
  const toastFunctions = useToasterHelper()
  const { getFirebaseErrors } = useCreate()

  const [loading, setLoading] = useState(true)
  const [addingAccount, setAddingAccount] = useState(false)
  const [switchingAccount, setSwitchingAccount] = useState(false)
  const [activeScreen, setActiveScreen] = useState<ESwitchScreen>(ESwitchScreen.yourAccounts)
  const [innerType, setInnerType] = useState('password')
  const [profileToRemove, setProfileToRemove] = useState<IRemoveProfile | null>(null)

  const {
    watch,
    register,
    setValue,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: yupResolver(formschema),
    defaultValues: { ...FORM_DEFAULT },
  })

  const { append, fields } = useFieldArray({
    control,
    name: 'profiles',
  })
  const { remove: removeAccessFiled, fields: fieldsHasAccess } = useFieldArray({
    control,
    name: 'hasAccessProfiles',
  })

  useEffect(() => {
    const fetchAllProfiles = async () => {
      if (!userId) return

      try {
        setLoading(true)

        const switchProfiles: ISwitchProfile[] = []
        const hasAccessProfiles: IProfileHasAccess[] = []

        const myProfileSnap = await FirestoreService.getItem(COLLECTIONS.PROFILES.NAME, userId)
        let myProfileInDb: IProfile | null = null

        if (myProfileSnap.exists()) {
          myProfileInDb = ProfileModel.fromFirestoreDoc(myProfileSnap).toObject()
        }

        const { users, profiles } = await getProfileUsers(userId, myProfileInDb?.allowedUids || [])

        const hasAccessUsersIds: string[] = []
        profiles.forEach((item) => {
          const user = users.find((user) => user.id === item.id)
          if (user) {
            hasAccessUsersIds.push(user.id)
            hasAccessProfiles.push({
              userDocId: user?.id,
              name: getUserFullName(user),
              userType: user?.userType ?? null,
              userProfilePicture: user?.userProfilePicture ?? null,
              lastSignInTime: '',
            })
          }
        })

        if (hasAccessProfiles.length) {
          const usersLoginData = await getLoginInfoByIds(hasAccessUsersIds)

          usersLoginData.forEach((userLoginData) => {
            const profileIndex = hasAccessProfiles.findIndex(
              (profile) => profile.userDocId === userLoginData.uid
            )
            if (profileIndex >= 0) {
              hasAccessProfiles[profileIndex] = {
                ...hasAccessProfiles[profileIndex],
                lastSignInTime: userLoginData.lastSignInTime,
              }
            }
          })

          setValue('hasAccessProfiles', hasAccessProfiles)
        }

        myProfileInDb?.allowedUids.forEach((uid) => {
          const user = users.find((user) => user.id === uid)
          if (user?.id && user?.id !== userId) {
            switchProfiles.push({
              userDocId: user?.id,
              name: getUserFullName(user),
              userType: user?.userType ?? null,
              userProfilePicture: user?.userProfilePicture ?? null,
            })
          }
        })

        if (!switchProfiles.length) {
          return setValue('message', 'No profiles are added to this account')
        }

        setValue('profiles', switchProfiles)
      } catch (error) {
        console.error('error: ', error)
        toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      } finally {
        setLoading(false)
      }
    }
    fetchAllProfiles().then()
  }, [setValue, toastFunctions, userId])

  const getLoginInfoByIds = async (userIds: string[]): Promise<IRespLoginUserInfo[]> => {
    try {
      const res = await httpService({
        url: `get_users_login_info_by_ids`,
        method: 'POST',
        data: {
          userIds,
        },
      })
      return res.users || ([] as IRespLoginUserInfo[])
    } catch (e) {
      console.error(e)
      return []
    }
  }

  const getProfiles = async () => {
    const profilesSnaps = await FirestoreService.getItem(COLLECTIONS.PROFILES.NAME, userId ?? '')
    const profile = getConvertedData(ProfileModel.fromFirestoreDoc(profilesSnaps).toObject())

    const { users } = await getProfileUsers(userId!, profile?.allowedUids || [])

    const switchProfiles: ISwitchProfile[] = []

    profile.allowedUids.forEach((uid) => {
      const user = users.find((user) => user.id === uid)
      const hasUser = fields.find((existUser) => existUser.userDocId === user?.id)
      if (user?.id && user?.id !== userId && !hasUser) {
        switchProfiles.push({
          userDocId: user?.id,
          name: getUserFullName(user),
          userType: user?.userType ?? null,
          userProfilePicture: user?.userProfilePicture ?? null,
        })
      }
    })

    return switchProfiles
  }

  const onSwitchAccountButtonClick = async () => {
    try {
      if (typeof watch('selectedProfileIndex') !== 'number') {
        throw new Error('Please select a profile to proceed further')
      }

      setSwitchingAccount(true)

      const res = await httpService({
        url: `get_switch_profile_token/${fields[watch('selectedProfileIndex')!].userDocId}`,
        method: 'GET',
      })

      const auth = getAuth()
      const userCredentials = await signInWithCustomToken(auth, res.data.token)

      const user_data_doc = await FirestoreService.getItem(
        CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.USERS.NAME,
        userCredentials.user.uid
      )

      const userData = UserModel.fromFirestoreDoc(user_data_doc).toObject()

      dispatch(setProfileDetails(userData))

      if (userData.userType === CONST.USE_MODE.COMPETITOR) {
        dispatch(storeUseMode(CONST.USE_MODE.COMPETITOR))
        history.replace('/home', { direction: 'none' })
      } else if (userData.userType === CONST.USE_MODE.ORGANIZER) {
        dispatch(storeUseMode(CONST.USE_MODE.ORGANIZER))
        history.replace(`${CONST.ROUTES.ORGANIZER_HOME.URL}`, { direction: 'none' })
      }

      dispatch(storeUserId(userCredentials.user.uid))
      dispatch(setUserId(userCredentials.user.uid))

      await auth.updateCurrentUser(userCredentials.user)

      props.handleModal(false, MODAL_CONSTS.SWITCH_ACCOUNT)
    } catch (error: any) {
      toastFunctions.error({ message: error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setSwitchingAccount(false)
    }
  }

  const onSubmit = async (data: any) => {
    data = data.formData

    setAddingAccount(true)

    try {
      const auth = getAuth()
      const user = auth.currentUser
      const credential = EmailAuthProvider.credential(data.email, data.password)

      const updatedUser = await signInWithCredential(auth, credential)
      const updatedUserUid = updatedUser.user.uid

      await auth.updateCurrentUser(user)

      await httpService({
        url: 'add_profile',
        method: 'POST',
        data: {
          currentUserId: userId,
          profileId: updatedUserUid,
        },
      })

      const profiles = await getProfiles()
      append(profiles)

      toastFunctions.success({
        message: MESSAGES_CONST.ACCOUNT_ADDED,
      })

      setActiveScreen(ESwitchScreen.yourAccounts)
    } catch (error: any) {
      console.error(error, 'error')
      let message = getFirebaseErrors(error)

      if (error instanceof AxiosError) {
        message = error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG
      } else if (error?.message) {
        message = error?.message
      }

      return toastFunctions.error({ message })
    } finally {
      setAddingAccount(false)
    }
  }

  const [selected, setSelected] = useState(false)

  const cancelHandler = () => {
    switch (activeScreen) {
      case ESwitchScreen.yourAccounts:
      case ESwitchScreen.whoHasAccess:
        return props.handleModal(false, MODAL_CONSTS.SWITCH_ACCOUNT)

      case ESwitchScreen.confirmRemoveAccount:
        return setActiveScreen(ESwitchScreen.whoHasAccess)

      default:
        return setActiveScreen(ESwitchScreen.yourAccounts)
    }
  }

  const isMainScreen = [ESwitchScreen.yourAccounts, ESwitchScreen.whoHasAccess].includes(
    activeScreen
  )

  const removeProfileHasAccess = async () => {
    if (!userId || !profileToRemove) return

    setSwitchingAccount(true)
    try {
      const { id, index } = profileToRemove
      const profilesSnaps = await FirestoreService.getItem(COLLECTIONS.PROFILES.NAME, id)
      const profile = getConvertedData(ProfileModel.fromFirestoreDoc(profilesSnaps).toObject())

      const allowedUidsToUpdate = profile.allowedUids.filter((uid) => uid !== userId)
      await FirestoreService.updateItem(COLLECTIONS.PROFILES.NAME, id, {
        allowedUids: allowedUidsToUpdate,
      })
      removeAccessFiled(index)
      setProfileToRemove(null)
      setActiveScreen(ESwitchScreen.whoHasAccess)

      toastFunctions.success({
        message: MESSAGES_CONST.ACCOUNT_REMOVED,
      })
    } catch (e) {
      console.error(e)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setSwitchingAccount(false)
    }
  }

  const getButtonProps = (
    label: string,
    onClick: () => void,
    loading: boolean,
    disabled: boolean = false,
    additionalClasses: string = ''
  ) => ({
    label,
    bgClass: 'bg-SeabiscuitMainThemeColor',
    textClass: 'text-white',
    className: `!md:max-w-[400px] !w-full m-4 !h-[48px] ${additionalClasses}`,
    onClick,
    loading,
    disabled,
  })

  const modalButtons = [
    ...(activeScreen === ESwitchScreen.yourAccounts
      ? [
          getButtonProps(
            'SWITCH',
            onSwitchAccountButtonClick,
            switchingAccount,
            typeof watch('selectedProfileIndex') !== 'number'
          ),
        ]
      : []),

    ...(activeScreen === ESwitchScreen.confirmRemoveAccount
      ? [getButtonProps('REMOVE', removeProfileHasAccess, switchingAccount)]
      : []),

    ...(activeScreen === ESwitchScreen.addAccount && selected
      ? [
          getButtonProps(
            'ADD ACCOUNT',
            () => handleSubmit(onSubmit)(),
            addingAccount,
            false,
            'border border-solid !border-SeabiscuitMainThemeColor'
          ),
        ]
      : []),

    {
      label: 'CANCEL',
      className: '!md:max-w-[400px] !w-full m-4 !h-[48px]',
      bgClass: '!bg-SeabiscuitLightThemeColor',
      borderClass: 'border border-transparent',
      textClass: '!text-SeabiscuitLightTextColor',
      onClick: cancelHandler,
    },
  ]

  return (
    <MainModal
      show={props.show}
      title={
        activeScreen === ESwitchScreen.confirmRemoveAccount ? 'Remove account' : 'Switch accounts'
      }
      type="SWITCH_ACCOUNT"
      setHeightAsPerContent={true}
      className="!px-0 !my-8 "
      size="md"
      buttons={modalButtons}
    >
      <div>
        {isMainScreen && (
          <>
            <SwitchAccountTabs activeTab={activeScreen} clickHandler={setActiveScreen} />
            {activeScreen === ESwitchScreen.yourAccounts ? (
              <SwitchAccountFirstStep
                selectedIndex={watch('selectedProfileIndex')}
                loading={loading}
                fields={fields as ISwitchProfile[]}
                selectHandler={(index) => {
                  setValue(
                    'selectedProfileIndex',
                    watch('selectedProfileIndex') === index ? null : index
                  )
                }}
                addAccountHandler={() => setActiveScreen(ESwitchScreen.addAccount)}
                notAvailableText={watch('message')}
              />
            ) : (
              <SwitchAccountWhoHasAccess
                profiles={fieldsHasAccess as IProfileHasAccess[]}
                removeProfile={(data) => {
                  setProfileToRemove(data)
                  setActiveScreen(ESwitchScreen.confirmRemoveAccount)
                }}
              />
            )}
          </>
        )}

        {activeScreen === ESwitchScreen.confirmRemoveAccount && (
          <div className={'text-black min-h-[306px]'}>
            <p>By clicking ‘Remove’, this account will no longer have access to your account. </p>
            <p className={'mt-4'}>Are you sure you want to remove their access?</p>
          </div>
        )}

        {activeScreen === ESwitchScreen.addAccount && (
          <>
            <form className="flex flex-col justify-between py-3" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <div className="AccountEmail flex flex-wrap items-center ">
                  <div className="flex-grow mx-14">
                    <label className="mb-3 !text-SeabiscuitDark200ThemeColor flex items-center gap-2 w-full px-4 py-2 border-solid rounded-xl border-SeabiscuitLightThemeColorD3 border-[1px] bg-transparent">
                      <span className="w-[22px]">
                        <img src="/assets/og_icons/Mail-1.svg" alt="icon" className="" />
                      </span>
                      <input
                        {...register('formData.email')}
                        placeholder="Enter Email"
                        className="flex-1 border-0 outline-0 !text-SeabiscuitDark200ThemeColor !bg-white placeholder:text-SeabiscuitDark200ThemeColor w-full focus:border-none p-2"
                      />
                    </label>
                    {errors.formData?.email && (
                      <MessageHelperComp isError={true} message={errors.formData?.email.message} />
                    )}
                  </div>
                </div>

                <div className="AccountEmail flex flex-wrap items-center">
                  <div className="flex-grow mx-14">
                    <label className="mb-3 text-SeabiscuitDark200ThemeColor flex items-center gap-2 w-full px-4 py-2 border-solid rounded-xl border-SeabiscuitLightThemeColorD3 border-[1px] bg-transparent">
                      <span>
                        <img src="/assets/og_icons/Lock-1.svg" alt="icon" className="" />
                      </span>
                      <input
                        type={innerType}
                        {...register('formData.password')}
                        placeholder="Enter Password"
                        className="flex-1 border-0 outline-0 !text-SeabiscuitDark200ThemeColor bg-transparent placeholder:text-SeabiscuitDark200ThemeColor focus:ring-transparent w-full focus:border-none p-2"
                      />
                      <div
                        onClick={() => setInnerType(innerType === 'password' ? 'text' : 'password')}
                      >
                        {innerType === 'password' ? (
                          <VisibilityOffOutlined className="text-SeabiscuitDark200ThemeColor" />
                        ) : (
                          <Visibility className="text-SeabiscuitDark200ThemeColor" />
                        )}
                      </div>
                    </label>
                    {errors.formData?.password && (
                      <MessageHelperComp
                        isError={true}
                        message={errors.formData?.password.message}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="displineCheckBoxfilter mx-14 flex">
                <input
                  className="form-check-input rounded_checkboxes appearance-none h-4 w-4 border border-[#D3DAEE] bg-white checked:bg-pink-600 checked:border-none focus:outline-none focus:ring-0 focus:ring-offset-0 transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer"
                  type="checkbox"
                  name="checkbox"
                  checked={selected}
                  id="checkbox"
                  onChange={(e) => setSelected(e.target.checked)}
                />
                <label
                  htmlFor="checkbox"
                  className="labelFilter cursor-pointer text-SeabiscuitDark200ThemeColor text-sm"
                >
                  By ticking this box I confirm that I have been given permission to access this
                  account by the account owner, and I accept that I am personally responsible for
                  this accounts activity.
                </label>
              </div>
            </form>
          </>
        )}
      </div>
    </MainModal>
  )
}

export default SwitchAccounts
